/*eslint-disable operator-linebreak*/
import {
  addMinutes,
  formatDistance,
  isAfter,
  isBefore,
  isToday,
  isTomorrow,
  parseISO,
  subMinutes,
} from 'date-fns';
import padStart from 'lodash/padStart';
import memoizee from 'memoizee';
import moment, { Moment } from 'moment';
import { TFunction } from 'react-i18next';

import { debugNowTime } from '../hooks/useAutoRefresh';
import { dateFNSLocale } from './date';

const dateFnsToMomentReplacements: Record<string, string | any> = {
  "'T'": 'T',
  "'([^']*)'": '[$1]',
  'Y': 'g',
  'y': 'Y',
  'a': 'A',
  "aaaaa'm'": 'a',
  'p': 'LT',
  'pp': 'LTS',
  'PPPPp': 'LLLL',
  'PPp': 'LLL',
  'PPPp': 'LLL',
  'P': 'L',
  'E': 'i',
  'W': 'l',

  // day of month
  'd': 'D', // 1 2
  'do': 'Do', // 1st 2nd
  'dd': 'DD', // 01 02

  // day of week
  'e': 'd', // 0 1 => no equivalent
  'eo': 'do', // 1st, 2nd
  'io': 'do', // 1st, 2nd
  'eee': 'ddd', // Sun Mon
  'eeee': 'dddd', // Sunday Monday
  'eeeeee': 'dd', // Su Mo
  'EEE': 'ddd', // Sun Mon
  'EEEE': 'dddd', // Sunday Monday
  'EEEEE': 'dd', // Su Mo

  // day of year
  'D': 'DDD', // 1 2
  'Do': 'DDDo', // 1st 2nd
  'DDDDD': 'DDDD', // 001 002

  'xx': 'ZZ',
  'xxx': 'Z',
};

const momentToDateFnsReplacements: Record<string, string> = {
  '\\[([^\\]]*)\\]': "'$1'",
  'LT': 'p',
  'LTS': 'pp',
  'LLLL|llll': 'PPPPp',
  'LLL|lll': 'PPPp',
  'L|l': 'P',
  'Y': 'y',
  'g': 'Y',
  'G': 'R',
  'W': 'l',
  'E': 'i',

  // day of month
  'D': 'd', // 1 2
  'Do': 'do', // 1st 2nd
  'DD': 'dd', // 01 02

  // day of week
  'd': 'e', // 0 1 => no equivalent
  'do': 'eo', // 0th 1st
  'dd': 'eeeeee', // Su Mo
  'ddd': 'eee', // Sun Mon
  'dddd': 'eeee', // Sunday Monday

  // day of year
  'DDD': 'D', // 1 2
  'DDDo': 'Do', // 1st 2nd
  'DDDD': 'DDD', // 001 002

  'a': "aaaaa'm'",
  'A': 'a',
  'T': "'T'",
  'X': 't',
  'x': 'T',
  'ZZ|zz': 'xx',
  '[Zz]': 'xxx',
};

function parseAndReplace(str: string, replacements: Record<string, string | any>) {
  if (!str) return str;
  let rest = str;
  let result = '';

  while (rest) {
    let bestMatchReplacement = null;
    let bestMatchLength = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const [pattern, replacement] of Object.entries(replacements)) {
      const regex = new RegExp(`^${pattern}`); // match from start of remaining input
      const match = rest.match(regex);
      if (match && match[0].length > bestMatchLength) {
        bestMatchReplacement =
          typeof replacement === 'function'
            ? replacement(match[0])
            : replacement.replace('$1', match[1]);
        bestMatchLength = match[0].length;
      }
    }

    if (bestMatchReplacement) {
      result += bestMatchReplacement;
      rest = rest.slice(bestMatchLength);
    } else {
      // Skip 1 character
      result += rest[0];
      rest = rest.slice(1);
    }
  }
  return result;
}

export function convertDateFnsToMoment(format: string): string {
  return parseAndReplace(format, dateFnsToMomentReplacements);
}

export function convertMomentToDateFns(format: string): string {
  return parseAndReplace(format, momentToDateFnsReplacements);
}

/**
 * Detects if the format looks like a moment date, and converts it to a date-fns format
 * @param format
 * @returns
 */
export const ensureDateFnsFormat = memoizee(
  (format: string): string => {
    if (!format) return format;

    // Looks like moment ?
    if (
      format.includes('[') ||
      format.includes('Y') ||
      format.match(/ a$/) ||
      // format.includes('L')
      format.match(/L[LT]/) // More specific: LL, LT, LLL, etc. are moment
    ) {
      return convertMomentToDateFns(format);
    }

    return format;
  },
  { max: 1000 },
);

/**
 * Detects if the format looks like a date-fns date, and converts it to a moment format
 * @param format
 * @returns
 */
export const ensureMomentFormat = memoizee(
  (format: string): string => {
    if (!format) return format;

    // Looks like date-fns ?
    if (
      format.includes("'") ||
      format.includes('y') ||
      format.includes('aaa') ||
      format.includes('ee') ||
      format.includes('EE') ||
      format.includes('P') ||
      format.includes('p')
    ) {
      return convertDateFnsToMoment(format);
    }

    return format.replace('PPPp', 'LLL').replace('PPp', 'LLL');
  },
  { max: 1000 },
);

interface Dates {
  startDate: string;
  endDate: string;
}
interface DateOptions {
  minutesBefore: number;
  minutesAfter: number;
}

export function isNow({ startDate, endDate }: Dates, options: DateOptions): boolean {
  const { minutesBefore = 5, minutesAfter = 5 } = options || {};
  const now = new Date();

  return (
    isBefore(parseISO(startDate), addMinutes(now, minutesBefore)) &&
    isAfter(parseISO(endDate), subMinutes(now, minutesAfter))
  );
}

export function isUpcoming({ startDate }: { startDate: string }): boolean {
  const now = new Date();
  return isAfter(parseISO(startDate), now);
}

export function isFinished({ endDate }: { endDate: string }): boolean {
  const now = new Date();
  return isBefore(parseISO(endDate), now);
}

export function startsToday({ startDate }: { startDate: string }): boolean {
  return isToday(parseISO(startDate));
}

export function getWorkshopStatus(workshop: Dates, options: DateOptions): string {
  if (isFinished({ endDate: workshop.endDate })) return 'finished';
  if (isNow(workshop, options)) return 'now-soon';
  if (isTomorrow(parseISO(workshop.startDate))) return 'tomorrow';
  if (isUpcoming(workshop)) return 'upcoming';
  return 'upcoming';
}

export function fromNow(date: string): string | null {
  const now = debugNowTime || new Date();
  if (!date) return null;
  return formatDistance(parseISO(date), now, {
    addSuffix: true,
    locale: dateFNSLocale(),
  });
}

export function formatTimezoneDate(date: string, timezone: string): Moment | null {
  if (!date) return null;
  if (timezone) return moment(parseISO(date)).tz(timezone);
  return moment(parseISO(date));
}

export function durationInMinutes(startDate: string, endDate: string): number {
  return Math.floor((parseISO(endDate).getTime() - parseISO(startDate).getTime()) / (60 * 1000));
}

export function roundMinutesToQuarter(minutes: number): number {
  return Math.round(minutes / 15) * 15;
}

function formatDurationMinutes(minutes: number, t: TFunction<'translation'>) {
  const hours = Math.floor(minutes / 60);
  const minutesLeft = minutes - hours * 60;
  return t('dates.duration', { hours, minutes: padStart(minutesLeft.toString(), 2, '0') });
}

export function formatDateRangeDuration(
  startDate: string,
  endDate: string,
  t: TFunction<'translation'>,
): string | null {
  if (!startDate || !endDate) return null;
  const minutes = durationInMinutes(startDate, endDate);
  return formatDurationMinutes(minutes, t);
}

export const formatDateAgo = (
  date: Date | string,
  t: TFunction<'translation'>,
  long?: boolean,
): string | null => {
  const currentDate = moment();
  const formattedDate = moment(date);

  const diffMin = { key: 'min', value: currentDate.diff(formattedDate, 'm') };
  const diffHours = { key: 'hour', value: currentDate.diff(formattedDate, 'h') };
  const diffDays = { key: 'day', value: currentDate.diff(formattedDate, 'd') };
  const diffMonth = { key: 'month', value: currentDate.diff(formattedDate, 'M') };
  const diffYear = { key: 'year', value: currentDate.diff(formattedDate, 'y') };

  const timeDiff = [diffYear, diffMonth, diffDays, diffHours, diffMin];

  const relativeTime = timeDiff.find((diff) => diff.value > 0);

  return relativeTime
    ? t(`common.${long ? 'long-' : ''}relative-time.${relativeTime.key}`, {
        count: relativeTime.value,
      })
    : t('common.relative-time.min', { count: 1 });
};

// Detect if a format string is for date-fns or moment
export type DateFormatKind = 'date-fns' | 'moment' | 'unknown';

const hasAnyToken = (format: string, tokens: string[]): boolean =>
  tokens.some((token) => format.includes(token));

export function isMomentFormat(format: string): boolean {
  // Distinctive Moment tokens
  const momentDistinctTokens = [
    'YYYY',
    'DD',
    'dddd',
    'A',
    'Z',
    'ZZ',
    'LL',
    'ddd',
    'Do',
    '[h]',
    '[:]',
    'LT z',
  ];
  return hasAnyToken(format, momentDistinctTokens);
}

export function isDateFnsFormat(format: string): boolean {
  // Distinctive date-fns tokens
  const dateFnsDistinctTokens = [
    'yyyy',
    'do',
    'EEEE',
    'EE',
    'XXX',
    'xx',
    'PP',
    'p',
    'd',
    "'h'",
    "':'",
  ];
  return hasAnyToken(format, dateFnsDistinctTokens);
}

export function detectDateFormatKind(format: string): DateFormatKind {
  const isMoment = isMomentFormat(format);
  const isDateFns = isDateFnsFormat(format);

  if (isMoment && !isDateFns) return 'moment';
  if (isDateFns && !isMoment) return 'date-fns';

  // Tie-breaker for rare mixed strings
  if (format.includes('ddd Do')) return 'moment';
  if (format.includes('yyyy') || format.includes('EEEE')) return 'date-fns';
  if (format.includes('YYYY') || format.includes('dddd')) return 'moment';
  return 'unknown';
}
